import React, { memo, useState } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import TuneIcon from "@mui/icons-material/Tune";

import SelectedFilters from "components/CasesTable/SelectedFilters";
import { MoreFilterDialog } from "components/CasesTable/MoreFilter/MoreFilterDialog";
import { MachineFilter } from "components/CasesTable/Filters/MachineFilter";
import { PriorityFilter } from "components/CasesTable/Filters/PriorityFilter";
import { StatusFilter } from "components/CasesTable/Filters/StatusFilter";
import { SiteFilter } from "components/CasesTable/Filters/SiteFilter";
import { ComponentFilter } from "components/CasesTable/Filters/ComponentFilter";
import { FailureTypeFilter } from "components/CasesTable/Filters/FailureTypesFilter";
import { CaseLabelFilter } from "components/CasesTable/Filters/CaseLabelFilter";
import { SeverityTypeFilter } from "components/CasesTable/Filters/SeverityTypesFilter";
import { UserFilter } from "components/CasesTable/Filters/UserFilter";

export const CaseTableFilters = memo(function CaseTableFilters(props) {
  const { filters, setFilters, setFilterGroups, clearFilters } = props;

  const clearAllFilters = () => {
    if (filters && Object.entries(filters).length > 0) {
      clearFilters();
    }
  };
  const onSetFilters = (values, title) => setFilters(title, values);
  const [showMoreFilterDialog, setShowMoreFilterDialog] = useState(false);
  const dialogOnClose = (event, reason) => {
    if (reason !== "backdropClick") {
      setShowMoreFilterDialog(false);
    }
  };

  return (
    <>
      <FiltersPaper>
        <PaperHeader>Filtering</PaperHeader>
        <SelectContainer>
          <SelectWrappers>
            <PriorityFilter
              selectedPriorities={filters["Priority"] ?? []}
              onChangeSelectedPriorities={values =>
                onSetFilters(values, "Priority")
              }
              showChips={false}
            />
          </SelectWrappers>
          <SelectWrappers>
            <StatusFilter
              selectedStatuses={filters["Status"] ?? []}
              onChangeSelectedStatuses={values =>
                onSetFilters(values, "Status")
              }
              showChips={false}
            />
          </SelectWrappers>
          <SelectWrappers>
            <SiteFilter
              selectedSites={filters["Site"] ?? []}
              onChangeSelectedSites={values => onSetFilters(values, "Site")}
              showChips={false}
            />
          </SelectWrappers>
          <SelectWrappers>
            <MachineFilter
              selectedMachines={filters["Machine"] ?? []}
              selectedSites={filters["Site"] ?? []}
              onChangeSelectedMachines={values =>
                onSetFilters(values, "Machine")
              }
              showChips={false}
            />
          </SelectWrappers>
          <SelectWrappers>
            <ComponentFilter
              selectedComponents={filters["Component"] ?? []}
              onChangeSelectedComponents={values =>
                onSetFilters(values, "Component")
              }
              showChips={false}
            />
          </SelectWrappers>
          <SelectWrappers>
            <FailureTypeFilter
              selectedFailureTypes={filters["Failure Mode"] ?? []}
              onChangeSelectedFailureTypes={values =>
                onSetFilters(values, "Failure Mode")
              }
              showChips={false}
            />
          </SelectWrappers>
          <SelectWrappers>
            <CaseLabelFilter
              selectedCaseLabels={filters["Label"] ?? []}
              onChangeSelectedCaseLabels={values =>
                onSetFilters(values, "Label")
              }
              showChips={false}
            />
          </SelectWrappers>
          <SelectWrappers>
            <SeverityTypeFilter
              title="Inspection"
              selectedSeverities={filters["Inspection"] ?? []}
              onChangeSelectedSeverities={values =>
                onSetFilters(values, "Inspection")
              }
              showChips={false}
            />
          </SelectWrappers>
          <SelectWrappers>
            <UserFilter
              title="Assignee"
              selectedUsers={filters["Assignee"] ?? []}
              onChangeSelectedUsers={values => onSetFilters(values, "Assignee")}
              showChips={false}
              emptyOption="unassigned"
            />
          </SelectWrappers>
          <SelectWrappers>
            <MoreFilterButton
              variant="outlined"
              color="primary"
              startIcon={<TuneIcon />}
              onClick={() => setShowMoreFilterDialog(true)}
            >
              More filters
            </MoreFilterButton>
          </SelectWrappers>
        </SelectContainer>
        <Stack direction="row" gap={1.5} mt={1}>
          <SelectedFilters
            selectedFilterValues={filters}
            onSetFilters={onSetFilters}
          />
          <ClearFiltersButton
            variant="outlined"
            color="primary"
            onClick={clearAllFilters}
            sx={{ margin: 0 }}
          >
            Clear Filters
          </ClearFiltersButton>
        </Stack>
      </FiltersPaper>
      <MoreFilterDialog
        openDialog={showMoreFilterDialog}
        handleClose={() => setShowMoreFilterDialog(false)}
        dialogOnClose={dialogOnClose}
        caseTableFilters={filters}
        onSetFilters={setFilterGroups}
        clearAllFilters={clearAllFilters}
      />
    </>
  );
});

CaseTableFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  setFilterGroups: PropTypes.func,
  clearFilters: PropTypes.func.isRequired,
  isDDTPage: PropTypes.bool
};

const FiltersPaper = styled(Paper)`
  padding: 16px;
  margin-bottom: 20px;
`;

const SelectContainer = styled("div")`
  display: flex;
  flex-wrap: wrap;
  margin-left: -5px;
  margin-right: -5px;
  justify-content: end;
`;

const SelectWrappers = styled("div")`
  flex: 1 1 8%;
  margin: 5px;
  @media (max-width: 1900px) {
    flex: 1 1 18%;
  }
  @media (max-width: 1200px) {
    flex: 1 1 45%;
  }
  @media (max-width: 960px) {
    flex: 1 1 18%;
  }
  @media (max-width: 860px) {
    flex: 1 1 45%;
  }
`;

const ClearFiltersButton = styled(Button)`
  min-width: 140px;
  margin: 12px 0 0 10px;
  max-height: 40px;
`;

const PaperHeader = styled(Typography)`
  font-size: 18px;
  color: #555555;
`;

const MoreFilterButton = styled(Button)`
  width: 100%;
  height: 100%;
  white-space: pre;
`;
